<template>
  <div class="filter flex flex-col justify-between">
    <div v-if="isShowSubFilter" class="sub-filter flex items-center justify-between pl-2 py-2 pr-3">
      <categories-selector class="" />
      <mobile-search-button />
    </div>
    <div class="main-filter flex justify-between items-end py-3 px-5 md:py-4 md:px-0 overflow-auto">
      <div class="filter-items flex gap-2 flex-nowrap md:flex-wrap justify-start">
        <template v-for="item in filterLayout" :key="item.name">
          <component
            :is="filterWrapperComponent"
            :show-clear="item.clearable && isFilterChanged"
            :title="item.label"
            :product-count="Number(props.productCount)"
            :trigger-icon="item.icon"
            :trigger-label="getFilterItemLabel(item)"
            :trigger-active="filterActiveState[item.name]"
            :is-loading="props.isLoading"
            @clear="handleFilterClear(item.name)"
            @submit="handleSubmit"
            @close="handleFilterClose(item.name)"
          >
            <template #default="{ visible }">
              <component
                :is="getFilterItemComponent(item.type)"
                v-model="filterState[item.name]"
                v-bind="/* @ts-ignore */ filterItemOption[item.name]"
                :visible="visible"
              />
            </template>
          </component>
        </template>
      </div>
      <div v-if="!isShowSubFilter" class="order flex items-center">
        <span class="text-base whitespace-nowrap mr-1">{{ $t('all_sorting_with_ punctuation') }}</span>
        <filter-item-select v-model="filterState.order" v-bind="filterItemOption[FilterTypeEnum.ORDER]" class="w-192px" />
      </div>
    </div>
    <slot name="extra"></slot>
    <div v-if="isShowSubFilter" class="sub-filter flex items-center justify-end px-3">
      <filter-item-select v-model="filterState.order" mode="icon" v-bind="filterItemOption[FilterTypeEnum.ORDER]" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { format, parseISO } from 'date-fns'

import { useAppStore } from '@/store/app'
import FilterPopup from '@/components/search/filter/filter-popup.vue'
import FilterDrawer from '@/components/search/filter/filter-drawer.vue'
import FilterItemCheckbox from '@/components/search/filter/filter-item-checkbox.vue'
import FilterItemRadio from '@/components/search/filter/filter-item-radio.vue'
import FilterItemRange from '@/components/search/filter/filter-item-range.vue'
import FilterItemDateRange from '@/components/search/filter/filter-item-date-range.vue'
import FilterItemSelect from '@/components/search/filter/filter-item-select.vue'
import MobileSearchButton from '@/components/search/mobile-search-button.vue'
import CategoriesSelector from '@/components/common/categories-selector.vue'

import type { FilterComponentProps } from '@/types/filters'
import { FilterUiTypeEnum, FilterTypeEnum, CurrencyEnum } from '@/types/filters'
import type { SfilterTag } from '@/types/api'

const app = useAppStore()

// 外部頁面的狀態
const pageFilterState = defineModel<FilterComponentProps>({ required: true })

const props = defineProps<{
  productCount: number | null | undefined
  isLoading: boolean
}>()

const emit = defineEmits({
  update: (value: FilterComponentProps) => value,
  submit: () => true,
})

const isShowSubFilter = computed(() => app.isMobile || app.isTablet)
// filter 相關
const { filterItemOption, filterLayout, filterState, filterStateUnWrap, isOrderChanged, reset, clear, saveLastSubmitted } = useSearchFilter()

// UI
const filterWrapperComponent = computed(() => (app.isMobile || app.isTablet ? FilterDrawer : FilterPopup))

const FILTER_ITEM_COMPONENT_MAP = {
  [FilterUiTypeEnum.CHECK_BOX]: FilterItemCheckbox,
  [FilterUiTypeEnum.RADIO]: FilterItemRadio,
  [FilterUiTypeEnum.RANGE]: FilterItemRange,
  [FilterUiTypeEnum.DATE_RANGE]: FilterItemDateRange,
  [FilterUiTypeEnum.SELECT]: FilterItemSelect,
}

const isFilterChanged = computed(() => filterStateUnWrap.value !== pageFilterState.value)

const getFilterItemComponent = (type: FilterUiTypeEnum) => FILTER_ITEM_COMPONENT_MAP[type] || 'div'

const getFilterItemLabel = (item: (typeof filterLayout.value)[number]) => {
  switch (item.name) {
    case FilterTypeEnum.DATE: {
      if (filterState.value.date.includes(null!)) return item.label

      return filterState.value.date
        ?.map?.(date => {
          const safeDate = (typeof date === 'string' && parseISO(date)) || date
          return format(safeDate as Date | number, 'yyyy/MM/dd')
        })
        ?.join(' - ')
    }

    case FilterTypeEnum.PRICE: {
      if (filterState.value.price[0] === null && filterState.value.price[1] === null) return item.label
      const currencySymbol = CurrencyEnum[filterItemOption.value.price.currencyId]
      const limit = [filterItemOption.value.price.min, filterItemOption.value[FilterTypeEnum.PRICE].max]
      const [min, max] = filterState.value.price

      return `${currencySymbol} ${priceFormat(min || limit[0], app.locale)} - ${priceFormat(max || limit[1], app.locale)}${max ? '' : '+'}`
    }

    case FilterTypeEnum.AGE:
    default: {
      const state = filterState.value[item.name] as number[]
      const optionTags = filterItemOption.value[item.name]?.options?.tags || []
      if (!item.name || !state || state.length === 0) return item.label || ''
      if (state.length > 2) return `${item.label}(${state.length})`

      return state.map(f => optionTags.find((t: SfilterTag) => f === t.id)?.name).join(', ')
    }
  }
}

const filterActiveState = computed(() =>
  Object.entries(filterState.value).reduce(
    (acc, [key, value]) => {
      if (Array.isArray(value)) {
        acc[key as FilterTypeEnum] = value.length > 0 && value.some(v => !!v)
      } else {
        acc[key as FilterTypeEnum] = !!value
      }
      return acc
    },
    {} as Record<string, boolean>
  )
)

// filter item event handlers
const handleFilterClose = (name: string) => reset(name)

const handleFilterClear = (name: string) => clear(name)

const handleUpdate = () => {
  pageFilterState.value = filterStateUnWrap.value as FilterComponentProps
  emit('update', filterStateUnWrap.value as FilterComponentProps)

  if (isOrderChanged.value) handleSubmit()
}

const handleSubmit = () => {
  saveLastSubmitted()
  emit('submit')
}

watch(filterState, handleUpdate, { deep: true })
</script>

<script lang="ts">
export default {
  name: 'SearchFilter',
}
</script>

<style lang="scss" scoped></style>
